import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Plus } from "lucide-react";
import { cn } from "../util";
import { calculateIntelligentPosition, measureElement } from "../util";
import { Tag } from "./tag";
import type { TagSearchResult, TagAutocompleteState } from "../types";

interface TagAutocompleteProps {
  autocompleteState: TagAutocompleteState;
  onSelect: (result: TagSearchResult) => void;
  onSelectByIndex?: (index: number) => void;
  onCreateNew?: (name: string) => void;
  onClose: () => void;
  className?: string;
  portalContainer?: HTMLElement; // Optional container to portal the dropdown to (e.g., document.body)
}

export function TagAutocomplete({
  autocompleteState,
  onSelect,
  onSelectByIndex,
  onCreateNew,
  onClose,
  className,
  portalContainer,
}: TagAutocompleteProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [intelligentPosition, setIntelligentPosition] = useState<{
    x: number;
    y: number;
    placement: { horizontal: "left" | "right"; vertical: "above" | "below" };
  } | null>(null);

  // Calculate intelligent positioning when autocomplete opens or position changes
  useEffect(() => {
    if (containerRef.current && autocompleteState.isOpen) {
      const container = containerRef.current;

      // First, temporarily position the element to measure it
      container.style.left = "-9999px";
      container.style.top = "-9999px";
      container.style.visibility = "hidden";

      // Measure the element
      const dimensions = measureElement(container, {
        maxWidth: 380, // Max width for dropdown (increased to match w-72)
        maxHeight: 400, // Max height for dropdown
      });

      // Calculate intelligent position
      const position = calculateIntelligentPosition({
        cursorPosition: autocompleteState.position,
        elementSize: dimensions,
        preferredPlacement: {
          horizontal: "right",
          vertical: "below",
        },
        offset: { x: 0, y: 4 },
        padding: 8,
      });

      setIntelligentPosition(position);

      // Apply the calculated position
      container.style.left = `${position.x}px`;
      container.style.top = `${position.y}px`;
      container.style.visibility = "visible";
    }
  }, [
    autocompleteState.position,
    autocompleteState.isOpen,
    autocompleteState.results,
  ]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (autocompleteState.isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [autocompleteState.isOpen, onClose]);

  if (!autocompleteState.isOpen) {
    return null;
  }

  const { results, selectedIndex, query } = autocompleteState;
  const trimmedQuery = query.trim();
  const hasResults = results.length > 0;
  const canCreateNew = trimmedQuery.length >= 2 && onCreateNew;
  const showCreateNew =
    canCreateNew &&
    !results.some(
      (r) => r.tag.name.toLowerCase() === trimmedQuery.toLowerCase(),
    );

  // Handle ref callback for both containerRef and event listener cleanup
  const handleRef = useCallback((el: HTMLDivElement | null) => {
    // @ts-ignore - Allow assignment to ref
    containerRef.current = el;

    if (el && portalContainer) {
      // When portaled, stop click propagation in capture phase
      const stopPropagation = (e: Event) => e.stopPropagation();
      el.addEventListener('mousedown', stopPropagation, true);
      return () => el.removeEventListener('mousedown', stopPropagation, true);
    }
  }, [portalContainer]);

  const dropdown = (
    <div
      ref={handleRef}
      className={cn(
        "absolute z-50 w-72 bg-popover border border-border rounded-md shadow-xl overflow-hidden",
        "transition-all duration-200 ease-out box-shadow-lg",
        // Add placement-based classes for potential styling
        intelligentPosition && [
          `placement-${intelligentPosition.placement.horizontal}`,
          `placement-${intelligentPosition.placement.vertical}`,
        ],
        className,
      )}
      style={{
        // backgroundColor: "var(--background)",
        backdropFilter: "blur(8px)",
      }}
    >
      {/* Placement indicator arrow */}
      {intelligentPosition && (
        <div
          className={cn(
            "absolute w-2 h-2 bg-popover border transform rotate-45",
            // Position arrow based on placement
            intelligentPosition.placement.vertical === "above" &&
              "bottom-[-4px] border-b border-r border-border",
            intelligentPosition.placement.vertical === "below" &&
              "top-[-4px] border-t border-l border-border",
            intelligentPosition.placement.horizontal === "left" && "right-3",
            intelligentPosition.placement.horizontal === "right" && "left-3",
          )}
          style={{
            backgroundColor: "var(--background)",
            borderColor: "var(--border)",
          }}
        />
      )}

      {/* Header */}
      <div className="px-3 py-2 bg-muted/30 border-b border-border">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-muted-foreground">
            Tags
          </span>
          {trimmedQuery && (
            <span className="text-xs text-muted-foreground">
              @{trimmedQuery}
            </span>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="max-h-48 overflow-y-auto">
        {hasResults ? (
          <div className="py-1">
            {results.map((result, index) => (
              <button
                key={result.tag.id}
                className={cn(
                  "w-full px-3 py-2 text-left hover:bg-muted transition-colors",
                  "flex items-center gap-2",
                  index === selectedIndex && "bg-muted",
                )}
                onClick={() => {
                  // Use onSelectByIndex if available (for TipTap integration), otherwise fallback to onSelect
                  if (onSelectByIndex) {
                    onSelectByIndex(index);
                  } else {
                    onSelect(result);
                  }
                }}
              >
                <Tag tag={result.tag} size="sm" variant="ghost" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-muted-foreground">
                    {result.matchType === "description" &&
                      result.tag.description && (
                        <span className="truncate block">
                          {result.tag.description}
                        </span>
                      )}
                    {result.matchType === "type" && (
                      <span>Type: {result.tag.type}</span>
                    )}
                  </div>
                </div>
                <div className="text-xs text-muted-foreground">
                  {Math.round(result.matchScore * 100)}%
                </div>
              </button>
            ))}
          </div>
        ) : trimmedQuery ? (
          <div className="px-3 py-4 text-center text-sm text-muted-foreground">
            No tags found for "{trimmedQuery}"
          </div>
        ) : (
          <div className="px-3 py-4 text-center text-sm text-muted-foreground">
            Start typing to search tags...
          </div>
        )}

        {/* Create new option */}
        {showCreateNew && (
          <>
            <div className="border-t border-border"></div>
            <button
              className={cn(
                "w-full px-3 py-2 text-left hover:bg-muted transition-colors",
                "flex items-center gap-2 text-sm",
                results.length === selectedIndex && "bg-muted",
              )}
              onClick={() => onCreateNew(trimmedQuery)}
            >
              <div className="w-5 h-5 rounded border-2 border-dashed border-muted-foreground/50 flex items-center justify-center">
                <Plus className="w-3 h-3" />
              </div>
              <span>Create "{trimmedQuery}"</span>
            </button>
          </>
        )}
      </div>

      {/* Footer with navigation hint */}
      <div className="px-3 py-2 bg-muted/30 border-t border-border">
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 bg-card border rounded text-xs">
              ↑↓
            </kbd>
            Navigate
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 bg-card border rounded text-xs">
              Enter
            </kbd>
            Select
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 bg-card border rounded text-xs">
              Esc
            </kbd>
            Cancel
          </span>
        </div>
      </div>
    </div>
  );

  // Use portal if container is provided (to escape transformed ancestors)
  if (portalContainer) {
    return createPortal(dropdown, portalContainer);
  }

  return dropdown;
}
